import { getLocalAppSession } from './storageHelpers';
import { capitalize, textToHtml } from './baseHelpers';
import { generatePromptKeywords } from './vacancy-generator/prompts';
import { authHeader } from './usableAuthToken';

type ChatGPTAgent = 'user' | 'system' | 'assistant';

export interface ChatGPTMessage {
  role: ChatGPTAgent;
  content: string;
}

interface OpenAIThreadsPayload {
  identifier: string;
  messages: ChatGPTMessage[];
  thread_id?: string;
  vacancy_id: number;
  stream?: boolean;
}

type OpenAIThreadsResponse = { thread_id: string; response: string };

// A real Error that names the status. statusText alone is the empty string
// over HTTP/2, which reached Sentry as "No error message" (SYM-6753).
const generateFailure = ({ ok, status, statusText }: Response) =>
  new Error(
    ok
      ? `AI generate failed: HTTP ${status} with an empty body`
      : `AI generate failed: HTTP ${status}${
          statusText ? ` ${statusText}` : ''
        }`
  );

export const getOpenAiThreadsResponse = ({
  onChange,
  signal,
  ...body
}: OpenAIThreadsPayload & {
  onChange?: (resp: string) => void;
  signal?: AbortSignal;
}) =>
  new Promise<OpenAIThreadsResponse>(async (resolve, reject) => {
    const response = await fetch(
      `${import.meta.env.VITE_BASE_URL_API}/v4/ai/generate`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Active-Customer': `${getLocalAppSession()?.activeCustomer}`,
          ...(await authHeader()),
        },
        body: JSON.stringify(body),
        signal,
      }
    );

    if (!response.ok || !response.body) {
      reject(generateFailure(response));
      return;
    }

    if (!body.stream || !onChange) {
      const result = await response.json();
      resolve(result?.data);
      return;
    }

    const output = await handleThreadsStream(response.body, (value) =>
      onChange(value)
    );
    resolve(output);
  });

export const getFormattedText = async (
  text: string,
  vacancyId: number,
  identifier: string,
  threadId: string
) => {
  try {
    const { response } = await getOpenAiThreadsResponse({
      identifier,
      vacancy_id: vacancyId,
      messages: [generatePromptKeywords()],
      thread_id: threadId,
      stream: false,
    });

    // Remove possible new line(s) from start of output
    if (text?.startsWith('\n')) text = text.slice(1);
    if (text?.startsWith('\n')) text = text.slice(1);
    if (text?.trim().length === 1) text = text.slice(1);
    if (text?.startsWith('\n')) text = text.slice(1);

    // Convert to html
    let html = textToHtml(text ?? '');

    // Get keywords from keywords output
    const keywords = response
      ?.split('\n')
      .map((word) => word.trim())
      .filter((word) => word) ?? [''];

    // Remove the numbering
    let formattedKeywords = keywords.flatMap(
      (word) => word.match(/[a-zA-Z]+/g) ?? ['']
    );
    const blacklistedKeywords = ['en'];

    formattedKeywords = formattedKeywords.filter(
      (word) => !blacklistedKeywords.includes(word)
    );

    // Extend array by adding duplicates of word in different casings
    formattedKeywords = [
      ...formattedKeywords,
      ...formattedKeywords.map((word) => capitalize(word)),
      ...formattedKeywords.map((word) => word.toLowerCase()),
    ];

    // If keywords were found, wrap them in strong tags
    formattedKeywords.forEach((word) => {
      html = html?.replaceAll(` ${word} `, ` <strong>${word}</strong> `);
      html = html?.replaceAll(` ${word},`, ` <strong>${word}</strong>,`);
      html = html?.replaceAll(` ${word}.`, ` <strong>${word}</strong>.`);
    });

    return html;
  } catch (error: any) {
    console.error(error);
    return text;
  }
};

const METADATA_PREFIXES = ['thread_id:', 'conversation_id:'] as const;

export const handleThreadsStream = async (
  stream: ReadableStream<Uint8Array>,
  onProgress: (value: string) => void
): Promise<OpenAIThreadsResponse> => {
  const reader = stream.getReader();
  const decoder = new TextDecoder('utf-8');
  let chunkValue = '';
  let pending = '';
  let threadId = '';
  let isReadingMetadata = true;

  const appendText = (text: string) => {
    if (text === '') return;

    chunkValue += text;
    onProgress(chunkValue);
  };

  const consumePending = (isFinalChunk: boolean) => {
    while (isReadingMetadata) {
      const prefix = METADATA_PREFIXES.find((candidate) =>
        pending.startsWith(candidate)
      );

      if (!prefix) {
        const isPartialMetadataPrefix = METADATA_PREFIXES.some((candidate) =>
          candidate.startsWith(pending)
        );

        if (isPartialMetadataPrefix && !isFinalChunk) return;

        isReadingMetadata = false;
        appendText(pending);
        pending = '';
        return;
      }

      const newlineIndex = pending.indexOf('\n');
      if (newlineIndex === -1) {
        if (!isFinalChunk) return;

        const id = pending.slice(prefix.length).trim();
        if (id !== '' && (prefix === 'thread_id:' || threadId === '')) {
          threadId = id;
        }
        pending = '';
        return;
      }

      const id = pending.slice(prefix.length, newlineIndex).trim();
      if (id !== '' && (prefix === 'thread_id:' || threadId === '')) {
        threadId = id;
      }
      pending = pending.slice(newlineIndex + 1);
    }

    appendText(pending);
    pending = '';
  };

  while (true) {
    const { value, done } = await reader.read();
    pending += decoder.decode(value, { stream: !done });
    consumePending(done);

    if (done) break;
  }

  pending += decoder.decode();
  consumePending(true);

  return { thread_id: threadId, response: chunkValue };
};
